import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { CategoryService } from '../../question-bank/services/category.service';
import { QuestionCategory } from '../../question-bank/types/question-bank.types';
import { TutorialBankEditorService } from '../services/tutorial-bank-editor.service';
import { TutorialBankService } from '../services/tutorial-bank.service';
import { TutorialBankItem } from '../types/tutorial-bank.types';

export const tutorialBankKeys = {
  all: ['tutorial-bank'] as const,
  lists: () => [...tutorialBankKeys.all, 'list'] as const,
  list: (filters: { page?: number; limit?: number; categoryId?: string }) =>
    [...tutorialBankKeys.lists(), filters] as const,
  details: () => [...tutorialBankKeys.all, 'detail'] as const,
  detail: (id: string, categoryId?: string) => [...tutorialBankKeys.details(), id, categoryId] as const,
  progress: () => [...tutorialBankKeys.all, 'progress'] as const,
  progressDetail: (tutorialId: string) => [...tutorialBankKeys.progress(), tutorialId] as const,
};

export const useTutorialBank = (page = 1, limit = 10, categoryId?: string) => {
  // Local loading state to ensure UI shows loading indicator
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  const [categoriesMap, setCategoriesMap] = useState<Record<string, string>>({});

  // Track current page to prevent unnecessary refetches
  const pageRef = useRef(page);

  // Get query client for cache invalidation
  const queryClient = useQueryClient();

  // Query key for tutorial bank items - use a base key without page for invalidation
  const tutorialBankBaseKey = tutorialBankKeys.lists();
  const tutorialBankQueryKey = [...tutorialBankBaseKey, page, limit, categoryId];

  // Query key for categories
  const categoriesQueryKey = ['tutorial-bank-categories'];

  // Update page reference when page changes
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Fetch tutorial bank items with pagination
  const {
    data: itemsData,
    isLoading: isItemsLoading,
    isFetching: isItemsFetching,
    error: itemsError,
    refetch: refetchItems,
  } = useQuery({
    queryKey: tutorialBankQueryKey,
    queryFn: async () => {
      try {
        console.log(`Fetching tutorials for page ${page}${categoryId ? ` and category ${categoryId}` : ''}`);
        // Using service method with pagination
        const data = await TutorialBankService.getTutorials(page, limit, categoryId);
        console.log(data, data?.pagination?.totalItems);
        setTotalItems(data?.pagination?.totalItems);
        return data.items;
      } catch (error) {
        console.error('Error fetching tutorials:', error);
        throw error;
      }
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0, // Set to 0 to always refetch when page changes
    gcTime: 0,
  });

  // Initialize category service
  const categoryService = new CategoryService();

  // Fetch categories
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: categoriesQueryKey,
    queryFn: async () => {
      try {
        // Using instance method with tutorial type
        const data = await categoryService.getCategoryHierarchy(true, 'tutorial');

        // Create a flat map of category IDs to names
        const categoryMap: Record<string, string> = {};

        const processCategoryHierarchy = (categories: any[]) => {
          categories.forEach(category => {
            categoryMap[category.id] = category.name;
            if (category.subcategories && category.subcategories.length > 0) {
              processCategoryHierarchy(category.subcategories);
            }
          });
        };

        processCategoryHierarchy(data);
        setCategoriesMap(categoryMap);
        return data;
      } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
    },
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Update categories state when data changes
  useEffect(() => {
    if (categoriesData) {
      setCategories(categoriesData);
    }
  }, [categoriesData]);

  // Delete tutorial mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => TutorialBankEditorService.deleteTutorialBankItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tutorialBankBaseKey });
      toast.success('Tutorial deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting tutorial:', error);
      toast.error(`Failed to delete tutorial: ${error.message || 'Unknown error'}`);
    },
  });

  // Custom refresh function that ensures loading state is visible
  const refresh = useCallback(async () => {
    // Only refetch categories if they don't exist or are empty
    if (!categories || categories.length === 0) {
      await Promise.all([refetchItems(), refetchCategories()]);
    } else {
      // Just refetch items if categories are already loaded
      await refetchItems();
    }
  }, [categories, refetchItems, refetchCategories]);

  // Get category name by ID
  const getCategoryNameById = useCallback((id: string) => {
    return categoriesMap[id] || '';
  }, [categoriesMap]);

  return {
    items: itemsData || [],
    totalItems,
    loading: isItemsLoading || isItemsFetching,
    error: itemsError,
    categories,
    categoriesLoading: isCategoriesLoading,
    categoriesError,
    refresh,
    deleteTutorial: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    getCategoryNameById
  };
};

// Hook for tutorial details
export const useTutorialDetail = (tutorialId: string, categoryId: string) => {

  const isFetchingRef = useRef(false);
  const queryClient = useQueryClient();

  const {
    data: tutorial,
    isLoading,
    error,
    refetch,
  } = useQuery<TutorialBankItem, Error>({
    queryKey: tutorialBankKeys.detail(tutorialId, categoryId),
    queryFn: async () => {
      if (isFetchingRef.current) {
        const cachedData = queryClient.getQueryData<TutorialBankItem>(tutorialBankKeys.detail(tutorialId, categoryId));
        if (cachedData) {
          return cachedData;
        }
      }

      if (!isFetchingRef.current) {
        isFetchingRef.current = true;

        try {
          const tutorial = await TutorialBankService.getTutorialById(tutorialId);
          return tutorial;
        } finally {
          isFetchingRef.current = false;
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      const latestCachedData = queryClient.getQueryData<TutorialBankItem>(tutorialBankKeys.detail(tutorialId, categoryId));
      return latestCachedData || null;
    },
    enabled: !!tutorialId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Custom refresh function that ensures loading state is visible
  const refresh = useCallback(async () => {
    if (isFetchingRef.current) {
      console.log('Refresh skipped: Another fetch is already in progress');
      return;
    }

    isFetchingRef.current = true;

    try {
      await refetch();
    } finally {
      isFetchingRef.current = false;
    }
  }, [refetch]);

  return {
    tutorial,
    loading: isLoading,
    error,
    refresh,
  };
};

// Hook for tutorial progress
export const useTutorialProgress = (tutorialId: string, categoryId?: string) => {
  const queryClient = useQueryClient();
  // Fetch progress
  const {
    data: progress,
    isLoading: loadingProgress,
    error: progressError,
    refetch: refreshProgress,
  } = useQuery({
    queryKey: tutorialBankKeys.progressDetail(tutorialId),
    queryFn: () => TutorialBankService.getUserProgress(tutorialId),
    enabled: !!tutorialId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const cachedTutorials = queryClient.getQueryData<TutorialBankItem[]>(
    ["tutorial-bank-tutorials-by-category", categoryId]
  );

  const startSessionMutation = useMutation({
    mutationFn: () => TutorialBankService.startOrUpdateTutorialSession(tutorialId, categoryId),
    onSuccess: (data) => {
      queryClient.setQueryData(tutorialBankKeys.progressDetail(tutorialId), data);
    },
  });

  // Save answer mutation
  const saveAnswerMutation = useMutation({
    mutationFn: ({ progressId, answer }: { progressId: string; answer: any }) =>
      TutorialBankService.saveAnswer(progressId, answer),
    onSuccess: (data) => {
      queryClient.setQueryData(tutorialBankKeys.progressDetail(tutorialId), data);
    },
  });

  // Complete tutorial mutation
  const completeTutorialMutation = useMutation({
    mutationFn: (progressId: string) => TutorialBankService.completeTutorial(progressId),
    onSuccess: (data) => {
      queryClient.setQueryData(tutorialBankKeys.progressDetail(tutorialId), data);
      toast.success('Tutorial completed!');
    },
  });

  const [isLoadingNextTutorial, setIsLoadingNextTutorial] = useState(false);

  const { data: tutorials = [] } = useQuery({
    queryKey: ["tutorial-bank-tutorials-by-category", categoryId],
    queryFn: async () => {
      setIsLoadingNextTutorial(true);
      try {
        return await TutorialBankService.getTutorialsByCategory(categoryId || '');
      } finally {
        setIsLoadingNextTutorial(false);
      }
    },
    enabled: !!categoryId && !cachedTutorials,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  const nextTutorial = useMemo(() => {
    if (!tutorialId || (!cachedTutorials && !tutorials.length)) return null;

    const tutorialsList = cachedTutorials || tutorials;

    const currentIndex = tutorialsList.findIndex(tutorial => tutorial.id === tutorialId);

    if (currentIndex === -1 || currentIndex === tutorialsList.length - 1) {
      return null;
    }

    return tutorialsList[currentIndex + 1];
  }, [tutorialId, categoryId, cachedTutorials, tutorials]);

  return {
    progress,
    loadingProgress,
    progressError,
    refreshProgress,
    startSession: startSessionMutation.mutate,
    isStartingSession: startSessionMutation.isPending,
    saveAnswer: saveAnswerMutation.mutate,
    isSavingAnswer: saveAnswerMutation.isPending,
    completeTutorial: completeTutorialMutation.mutate,
    isCompletingTutorial: completeTutorialMutation.isPending,
    nextTutorial,
    isLoadingNextTutorial,
  };
};
